import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CampoLabel } from "@/components/NotaAiuto";
import { Switch } from "@/components/ui/switch";
import CodiceIvaPicker from "@/components/CodiceIvaPicker";
import ProvinciaPicker from "@/components/ProvinciaPicker";
import AddressItalyFields from "@/components/AddressItalyFields";
import CanaliPubblicazioneSection from "@/components/CanaliPubblicazioneSection";
import PublicApiSection from "@/components/PublicApiSection";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, ChevronLeft, ChevronDown, ChevronRight, Trash2, Pencil, Warehouse, Save, X, Truck, Link2, Power, PowerOff, Landmark, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useDoubleConfirm, useConfirm } from "@/components/ConfirmProvider";
import TravasoVeicoliWizard from "@/components/TravasoVeicoliWizard";
import CompletezzaStampeBanner from "@/components/CompletezzaStampeBanner";

const emptySiteForm = () => ({
  nome: "",
  indirizzo: { via: "", cap: "", comune: "", provincia: "" },
  telefono: "",
  email: "",
  attiva: true,
});

// Hotfix 4SED.HF — Estrazione messaggio errore SICURA per React rendering.
// I 422 strutturati hanno detail come oggetto: NEVER renderizzare l'oggetto.
// Restituisce sempre una stringa.
const errMsg = (e, fallback = "Errore") => {
  const d = e?.response?.data?.detail;
  if (d == null) return fallback;
  if (typeof d === "string") return d;
  if (typeof d === "object") return d.detail || d.error || fallback;
  return fallback;
};

export default function CompanyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const confirm = useDoubleConfirm();
  const singleConfirm = useConfirm();
  const [company, setCompany] = useState(null);
  const [sites, setSites] = useState([]);
  // Mappa companyId → nome breve (per badge "Condivisa con X, Y")
  const [companiesMap, setCompaniesMap] = useState({});
  // Sprint 4H-DEP.B — Dialog gestione depositi della sede
  const [depositiSiteId, setDepositiSiteId] = useState(null);
  const [siteOpen, setSiteOpen] = useState(false);
  const [siteEditingId, setSiteEditingId] = useState(null); // null = create, id = edit
  const [siteForm, setSiteForm] = useState(emptySiteForm());
  const [siteSaving, setSiteSaving] = useState(false);
  const [saving, setSaving] = useState(false);
  // Sprint 4SED.C — Dialog "Aggiungi azienda a sede"
  const [addCompanyDialog, setAddCompanyDialog] = useState(null); // { siteId } | null
  const [addCompanyId, setAddCompanyId] = useState("");
  const [addingCompany, setAddingCompany] = useState(false);
  // Sprint 4SED.C — Wizard travaso veicoli
  const [travaso, setTravaso] = useState(null); // {siteId, siteName, companyId, companyName, context, onAfterTransfer}
  // Sprint 4SED.E — espansione albero Sede>Deposito
  const [expandedSites, setExpandedSites] = useState(new Set());
  const toggleExpand = (sid) => {
    const next = new Set(expandedSites);
    if (next.has(sid)) next.delete(sid); else next.add(sid);
    setExpandedSites(next);
  };

  const load = async () => {
    try {
      const c = await api.get(`/companies/${id}`);
      setCompany(c.data);
      // Sprint 4SED.E — usa sites-tree per caricare sedi + depositi + conteggi
      const tree = await api.get(`/companies/${id}/sites-tree`);
      // Mappa {site_id → id} per retro-compatibilità con il resto della pagina
      const treeSites = ((tree.data || {}).sedi || []).map((s) => ({
        ...s,
        id: s.site_id,
        indirizzo: { comune: s.comune },
      }));
      setSites(treeSites);
      // Carica tutte le aziende per resolver chip/badge
      const all = await api.get(`/companies`);
      const map = {};
      (all.data || []).forEach((co) => {
        map[co.id] = co.nome_breve || co.name || co.ragione_sociale || co.id;
      });
      setCompaniesMap(map);
    } catch (e) { toast.error(errMsg(e, "Errore")); }
  };
  useEffect(() => { load(); }, [id]);

  const [savedTick, setSavedTick] = useState(0);
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...company };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      delete payload.deleted_at; delete payload.sites_count;
      delete payload.veicoli_stock; delete payload.veicoli_in_gestione; delete payload.veicoli_totale;
      delete payload.name; // legacy alias gestito da backend
      await api.patch(`/companies/${id}`, payload);
      setSavedTick((t) => t + 1);
      toast.success("Salvato");
    } catch (e) { toast.error(errMsg(e, "Errore")); }
    finally { setSaving(false); }
  };

  const removeCompany = async () => {
    const ok = await confirm({
      variant: "delete", title: "Elimina azienda",
      message: `Stai per eliminare l'azienda "${company?.nome_breve}". L'azienda sarà disattivata (soft-delete).`,
      confirmLabel: "SI, elimina",
    });
    if (!ok) return;
    const motivo = window.prompt("Motivo dell'eliminazione (obbligatorio):") || "";
    if (!motivo.trim()) { toast.error("Motivo obbligatorio"); return; }
    try {
      await api.delete(`/companies/${id}`, { data: { motivo } });
      toast.success("Azienda eliminata");
      nav("/companies");
    } catch (e) { toast.error(errMsg(e, "Errore")); }
  };

  const openCreateSite = () => {
    setSiteEditingId(null);
    setSiteForm(emptySiteForm());
    setSiteOpen(true);
  };

  const openEditSite = async (s) => {
    // Sprint 4SED.E — sites-tree non include tutti i campi address: fetch puntuale
    let full = s;
    try {
      const r = await api.get(`/sites/${s.id}`);
      full = r.data;
    } catch (_) { /* fallback ai dati tree */ }
    setSiteEditingId(s.id);
    setSiteForm({
      nome: full.nome || "",
      indirizzo: {
        via: full.indirizzo?.via || "",
        cap: full.indirizzo?.cap || "",
        comune: full.indirizzo?.comune || "",
        provincia: full.indirizzo?.provincia || "",
      },
      telefono: full.telefono || "",
      email: full.email || "",
      attiva: full.attiva !== false,
    });
    setSiteOpen(true);
  };

  const submitSite = async () => {
    if (!siteForm.nome.trim()) { toast.error("Nome obbligatorio"); return; }
    if (siteForm.email && !/^\S+@\S+\.\S+$/.test(siteForm.email)) {
      toast.error("Email non valida"); return;
    }
    setSiteSaving(true);
    try {
      if (siteEditingId) {
        await api.patch(`/sites/${siteEditingId}`, siteForm);
        toast.success("Sede aggiornata");
      } else {
        await api.post(`/companies/${id}/sites`, siteForm);
        toast.success("Sede creata");
      }
      setSiteOpen(false);
      setSiteEditingId(null);
      setSiteForm(emptySiteForm());
      load();
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    } finally {
      setSiteSaving(false);
    }
  };

  const removeSite = async (s) => {
    const ok = await confirm({
      variant: "delete", title: "Elimina sede",
      message: `Stai per eliminare la sede "${s.nome}".`,
      confirmLabel: "SI, elimina",
    });
    if (!ok) return;
    try {
      await api.delete(`/sites/${s.id}`, { data: { motivo: "user request" } });
      toast.success("Sede eliminata");
      load();
    } catch (e) { toast.error(errMsg(e, "Errore")); }
  };

  // ===== Sprint 4SED.C — Gestione aziende collegate alla sede (N:N) =====

  const getCompanyIds = (s) => {
    // Compat legacy: fallback a [company_id] se company_ids mancante
    const arr = Array.isArray(s.company_ids) ? s.company_ids : (s.company_id ? [s.company_id] : []);
    return arr;
  };

  const patchSiteCompanies = async (siteId, newIds) => {
    return api.patch(`/sites/${siteId}/companies`, { company_ids: newIds });
  };

  const handleAddCompanyToSite = async () => {
    if (!addCompanyId || !addCompanyDialog?.siteId) return;
    const site = sites.find((s) => s.id === addCompanyDialog.siteId);
    const cur = getCompanyIds(site);
    if (cur.includes(addCompanyId)) {
      toast.error("Azienda già collegata a questa sede");
      return;
    }
    setAddingCompany(true);
    try {
      await patchSiteCompanies(site.id, [...cur, addCompanyId]);
      toast.success("Azienda collegata alla sede");
      setAddCompanyDialog(null);
      setAddCompanyId("");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    } finally {
      setAddingCompany(false);
    }
  };

  const handleRemoveCompanyFromSite = async (site, cid) => {
    const cur = getCompanyIds(site);
    if (cur.length <= 1) {
      toast.error("Una sede deve appartenere ad almeno un'azienda. Aggiungi prima un'altra azienda.");
      return;
    }
    const newIds = cur.filter((c) => c !== cid);
    const companyLabel = companiesMap[cid] || cid;
    try {
      await patchSiteCompanies(site.id, newIds);
      toast.success(`Azienda "${companyLabel}" slegata dalla sede`);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // 422 strutturato company_has_vehicles_in_site → apri wizard travaso
      if (detail && typeof detail === "object"
          && detail.error === "company_has_vehicles_in_site") {
        const ok = await singleConfirm({
          variant: "approve",
          title: "Slega azienda — veicoli a stock presenti",
          message: (`L'azienda "${companyLabel}" ha ${detail.vehicle_count} veicoli a stock in questa sede. `
                   + "Per slegarla devi prima trasferirli ad un'altra sede."),
          confirmLabel: "Trasferisci veicoli →",
        });
        if (ok) {
          setTravaso({
            siteId: site.id, siteName: site.nome,
            companyId: cid, companyName: companyLabel,
            context: "slega_azienda",
            onAfterTransfer: async () => {
              // Tentativo di slega definitivo quando remaining=0
              try {
                await patchSiteCompanies(site.id, newIds);
                toast.success(`Azienda "${companyLabel}" slegata dalla sede`);
                await load();
              } catch (err) {
                toast.error(errMsg(err, "Errore slega azienda"));
              }
            },
          });
        }
      } else {
        toast.error(errMsg(e, "Errore slega azienda"));
      }
    }
  };

  // Toggle disattivazione sede con gestione blocco veicoli a stock
  const handleToggleSiteAttiva = async (s) => {
    const isDeactivating = s.attiva !== false;
    if (!isDeactivating) {
      // Riattivazione: nessun blocco
      try {
        await api.patch(`/sites/${s.id}`, { attiva: true });
        toast.success("Sede riattivata");
        await load();
      } catch (e) {
        toast.error(errMsg(e, "Errore riattivazione"));
      }
      return;
    }
    // Disattivazione: conferma
    const ok = await singleConfirm({
      variant: "approve",
      title: "Disattiva sede",
      message: (`Disattivare la sede "${s.nome}"? Resterà agganciata ai veicoli `
               + "venduti per lo storico, ma non sarà selezionabile per nuovi veicoli."),
      confirmLabel: "SI, disattiva",
    });
    if (!ok) return;
    try {
      await api.patch(`/sites/${s.id}`, { attiva: false });
      toast.success("Sede disattivata");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object"
          && detail.error === "site_has_transferable_vehicles") {
        // Determina su quali aziende ci sono ancora veicoli da spostare
        const cids = getCompanyIds(s);
        const ok2 = await singleConfirm({
          variant: "approve",
          title: "Sede con veicoli attivi",
          message: (`La sede contiene ${detail.transferable_vehicle_count} veicoli con pratica non consegnata. `
                  + "Trasferiscili prima di disattivare."),
          confirmLabel: "Trasferisci veicoli →",
        });
        if (ok2) {
          // Apre il wizard per la prima azienda con veicoli (in pratica spesso 1 sola).
          // L'utente potrà aprire il wizard per le altre aziende dalla lista.
          const firstCid = cids[0];
          setTravaso({
            siteId: s.id, siteName: s.nome,
            companyId: firstCid, companyName: companiesMap[firstCid] || firstCid,
            context: "disattiva_sede",
            onAfterTransfer: async () => {
              try {
                await api.patch(`/sites/${s.id}`, { attiva: false });
                toast.success("Sede disattivata");
                await load();
              } catch (err) {
                const d = err?.response?.data?.detail;
                if (d && typeof d === "object" && d.error === "site_has_transferable_vehicles") {
                  toast.error("Restano altri veicoli attivi di altre aziende. Apri il wizard di travaso per ciascuna azienda dalla riga sede.", { duration: 7000 });
                  await load();
                } else {
                  toast.error(errMsg(err, "Errore disattivazione"));
                }
              }
            },
          });
        }
      } else {
        toast.error(errMsg(e, "Errore disattivazione sede"));
      }
    }
  };

  if (!company) return <div className="text-slate-500">Caricamento...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/companies" className="text-slate-500 hover:text-slate-700"><ChevronLeft className="h-5 w-5" /></Link>
          <Building2 className="h-6 w-6 text-blue-600" />
          <h1 className="font-heading text-2xl font-semibold">{company.nome_breve || company.name}</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={removeCompany} className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="company-delete-btn">
            <Trash2 className="h-4 w-4 mr-1.5" /> Elimina
          </Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="company-save-btn">Salva</Button>
        </div>
      </div>

      <Tabs defaultValue="data">
        <TabsList>
          <TabsTrigger value="data" data-testid="company-tab-data">Dati Anagrafici e fiscali</TabsTrigger>
          <TabsTrigger value="sites" data-testid="company-tab-sites">Sedi Operative ({sites.length})</TabsTrigger>
          <TabsTrigger value="canali" data-testid="company-tab-canali">Canali di pubblicazione</TabsTrigger>
        </TabsList>

        <TabsContent value="canali" className="mt-4">
          <CanaliPubblicazioneSection companyId={id} />
          <PublicApiSection companyId={id} />
        </TabsContent>

        <TabsContent value="data" className="mt-4">
          <CompletezzaStampeBanner companyId={id} refreshKey={savedTick} />
          <Card className="p-6 border border-slate-200 shadow-sm bg-white">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                ["nome_breve", "Nome breve"], ["ragione_sociale", "Ragione sociale"],
                ["partita_iva", "P.IVA"], ["codice_fiscale", "Codice fiscale"],
                ["reg_imprese", "Reg. imprese"],
                ["foro_competente", "Foro competente"],
                ["telefono", "Telefono"], ["email", "Email"], ["pec", "PEC"], ["sito_web", "Sito web"],
              ].map(([key, label]) => (
                <div key={key}><CampoLabel campo={key}>{label}</CampoLabel>
                  <Input value={company[key] || ""} onChange={(e) => setCompany({ ...company, [key]: e.target.value })} data-testid={`detail-${key}`} /></div>
              ))}
              <div className="sm:col-span-2 pt-3 border-t">
                <Label className="text-xs font-medium uppercase tracking-wider text-slate-500">Configurazione fiscale</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  <div>
                    <CampoLabel campo="iva_codice_default">Codice IVA default</CampoLabel>
                    <CodiceIvaPicker
                      value={company.iva_codice_default}
                      onChange={(v) => setCompany({ ...company, iva_codice_default: v })}
                      testId="company-iva-codice-default"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      Usato come default per spese e voci di vendita senza IVA specifica.
                      Tipicamente "I22".
                    </p>
                  </div>
                </div>
              </div>
              {/* Sprint 4F2-CVT.A — Ritenuta acconto (intermediazione assicurativa) */}
              <RitenutaAccontoSection companyId={id} company={company} setCompany={setCompany} />
              <div className="sm:col-span-2 pt-3 border-t">
                <CampoLabel campo="indirizzo_legale" className="text-xs font-medium uppercase tracking-wider text-slate-500">Indirizzo legale (Italia)</CampoLabel>
                <div className="mt-2">
                  <AddressItalyFields
                    value={company.indirizzo_legale || {}}
                    onChange={(addr) => setCompany({ ...company, indirizzo_legale: addr })}
                    testIdPrefix="company-detail-addr"
                  />
                </div>
              </div>
              {/* Sprint AZIENDA-RIFINITURE — Referente Azienda */}
              <ReferenteAziendaSection company={company} setCompany={setCompany} />
              {/* Sprint AZIENDA-RIFINITURE — Conti Bancari multipli */}
              <ContiBancariSection company={company} setCompany={setCompany} />
              {/* Sprint STAMPE (§92) — Parametri contrattuali predefiniti */}
              <ParametriContrattualiSection company={company} setCompany={setCompany} />
              {/* Sprint STAMPE (§93 fix) — Logo per i documenti di stampa */}
              <LogoDocumentiSection companyId={id} company={company} setCompany={setCompany} />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="sites" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button onClick={openCreateSite} className="bg-blue-600 hover:bg-blue-700" data-testid="site-new-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Nuova sede
            </Button>
          </div>
          <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-slate-50 border-b text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 w-8"></th>
                    <th className="px-4 py-2.5">Nome</th>
                    <th className="px-4 py-2.5">Aziende collegate</th>
                    <th className="px-4 py-2.5">Comune</th>
                    <th className="px-3 py-2.5 text-right">Stock</th>
                    <th className="px-3 py-2.5 text-right">In gestione</th>
                    <th className="px-3 py-2.5 text-right">Totale</th>
                    <th className="px-4 py-2.5">Stato</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sites.map((s) => {
                    const cids = getCompanyIds(s);
                    const isShared = cids.length > 1;
                    const isActive = s.attiva !== false;
                    const cnt = s.conteggi || { stock: 0, in_gestione: 0, totale: 0 };
                    const isExpanded = expandedSites.has(s.id);
                    const depositi = s.depositi || [];
                    const hasDepositi = depositi.length > 0;
                    return (
                      <React.Fragment key={s.id}>
                        <tr className="hover:bg-slate-50" data-testid={`site-row-${s.id}`}>
                          <td className="px-2 py-3 align-top">
                            {hasDepositi ? (
                              <button
                                onClick={() => toggleExpand(s.id)}
                                className="text-slate-400 hover:text-slate-700"
                                data-testid={`site-expand-${s.id}`}
                                title={isExpanded ? "Comprimi depositi" : "Espandi depositi"}
                              >
                                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </button>
                            ) : (
                              <span className="block w-4 h-4" />
                            )}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <div className="font-medium">{s.nome}</div>
                            {isShared && (
                              <span
                                className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700"
                                title={`Condivisa con: ${cids.filter((c) => c !== id).map((c) => companiesMap[c] || c).join(", ")}`}
                                data-testid={`site-badge-condivisa-${s.id}`}
                              >
                                <Link2 className="h-3 w-3" /> Condivisa
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <div className="flex flex-wrap gap-1.5 items-center">
                              {cids.map((cid) => {
                                const label = companiesMap[cid] || cid.substring(0, 8);
                                const isCurrent = cid === id;
                                return (
                                  <span
                                    key={cid}
                                    className={
                                      "inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border " +
                                      (isCurrent
                                        ? "bg-blue-50 border-blue-200 text-blue-800"
                                        : "bg-slate-50 border-slate-200 text-slate-700")
                                    }
                                    data-testid={`site-${s.id}-chip-${cid}`}
                                  >
                                    {label}
                                    {cids.length > 1 && (
                                      <button
                                        onClick={() => handleRemoveCompanyFromSite(s, cid)}
                                        className="text-slate-400 hover:text-rose-600"
                                        title="Slega azienda dalla sede"
                                        data-testid={`site-${s.id}-chip-remove-${cid}`}
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    )}
                                  </span>
                                );
                              })}
                              <button
                                onClick={() => { setAddCompanyDialog({ siteId: s.id }); setAddCompanyId(""); }}
                                className="inline-flex items-center gap-0.5 text-[11px] px-2 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-500 hover:bg-slate-50 hover:text-blue-600 hover:border-blue-300"
                                title="Aggiungi azienda"
                                data-testid={`site-${s.id}-add-company`}
                              >
                                <Plus className="h-3 w-3" /> Azienda
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3 align-top text-slate-700">{s.indirizzo?.comune || "—"}</td>
                          <td className="px-3 py-3 align-top text-right tabular-nums text-emerald-700" data-testid={`site-cnt-stock-${s.id}`}>{cnt.stock}</td>
                          <td className="px-3 py-3 align-top text-right tabular-nums text-amber-700" data-testid={`site-cnt-in-gestione-${s.id}`}>{cnt.in_gestione}</td>
                          <td className="px-3 py-3 align-top text-right tabular-nums font-medium text-slate-900" data-testid={`site-cnt-totale-${s.id}`}>{cnt.totale}</td>
                          <td className="px-4 py-3 align-top">
                            {isActive ? (
                              <span className="text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid={`site-stato-${s.id}`}>Attiva</span>
                            ) : (
                              <span className="text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-200 text-slate-600" data-testid={`site-stato-${s.id}`}>Disattivata</span>
                            )}
                          </td>
                          <td className="px-4 py-3 align-top text-right">
                            <div className="flex items-center gap-3 justify-end">
                              <button
                                onClick={() => setTravaso({
                                  siteId: s.id, siteName: s.nome,
                                  companyId: id, companyName: companiesMap[id] || company?.nome_breve,
                                  context: "standalone",
                                })}
                                className="text-slate-500 hover:text-blue-600"
                                title="Trasferisci veicoli ad un'altra sede"
                                data-testid={`site-travaso-${s.id}`}
                              >
                                <Truck className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => setDepositiSiteId(s.id)}
                                className="text-slate-500 hover:text-blue-600"
                                title="Gestisci depositi della sede"
                                data-testid={`site-depositi-${s.id}`}
                              >
                                <Warehouse className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleToggleSiteAttiva(s)}
                                className={isActive ? "text-amber-600 hover:text-amber-700" : "text-emerald-600 hover:text-emerald-700"}
                                title={isActive ? "Disattiva sede" : "Riattiva sede"}
                                data-testid={`site-toggle-attiva-${s.id}`}
                              >
                                {isActive ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                              </button>
                              <button
                                onClick={() => openEditSite(s)}
                                className="text-slate-500 hover:text-blue-600"
                                title="Modifica sede"
                                data-testid={`site-edit-${s.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => removeSite(s)}
                                disabled={cnt.totale > 0}
                                className={
                                  cnt.totale > 0
                                    ? "text-rose-300 cursor-not-allowed"
                                    : "text-rose-600 hover:text-rose-700"
                                }
                                title={
                                  cnt.totale > 0
                                    ? "Non eliminabile: contiene veicoli. Disattivala oppure trasferisci i veicoli."
                                    : "Elimina sede"
                                }
                                data-testid={`site-del-${s.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && depositi.map((d) => {
                          const dc = d.conteggi || { stock: 0, in_gestione: 0, totale: 0 };
                          const isEmpty = dc.totale === 0;
                          return (
                            <tr key={d.deposito_id} className="bg-slate-50/40"
                                data-testid={`site-${s.id}-deposito-row-${d.deposito_id}`}>
                              <td></td>
                              <td className="pl-8 pr-4 py-2 text-[13px]" colSpan={3}>
                                <span className={isEmpty ? "text-slate-400" : "text-slate-700"}>
                                  <Warehouse className="inline h-3.5 w-3.5 mr-1.5 text-slate-400" />
                                  {d.nome}
                                </span>
                              </td>
                              <td className={"px-3 py-2 text-right tabular-nums " + (isEmpty ? "text-slate-300" : "text-emerald-700")}
                                  data-testid={`deposito-cnt-stock-${d.deposito_id}`}>{dc.stock}</td>
                              <td className={"px-3 py-2 text-right tabular-nums " + (isEmpty ? "text-slate-300" : "text-amber-700")}
                                  data-testid={`deposito-cnt-in-gestione-${d.deposito_id}`}>{dc.in_gestione}</td>
                              <td className={"px-3 py-2 text-right tabular-nums " + (isEmpty ? "text-slate-300" : "font-medium text-slate-900")}
                                  data-testid={`deposito-cnt-totale-${d.deposito_id}`}>{dc.totale}</td>
                              <td colSpan={2}></td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                  {sites.length === 0 && (
                    <tr><td colSpan={9} className="text-center py-8 text-slate-400">Nessuna sede</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={siteOpen} onOpenChange={(o) => { setSiteOpen(o); if (!o) setSiteEditingId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{siteEditingId ? "Modifica sede" : "Nuova sede"}</DialogTitle>
            <DialogDescription>
              {siteEditingId
                ? "Aggiorna i dati della sede selezionata."
                : "Inserisci i dati della nuova sede operativa."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nome *</Label>
              <Input
                value={siteForm.nome}
                onChange={(e) => setSiteForm({ ...siteForm, nome: e.target.value })}
                data-testid="site-input-nome"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Indirizzo (Italia)</Label>
              <div className="mt-1">
                <AddressItalyFields
                  value={siteForm.indirizzo}
                  onChange={(addr) => setSiteForm({ ...siteForm, indirizzo: addr })}
                  testIdPrefix="site-addr"
                  compact
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Telefono</Label>
                <Input value={siteForm.telefono} onChange={(e) => setSiteForm({ ...siteForm, telefono: e.target.value })} data-testid="site-input-tel" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input
                  type="email"
                  value={siteForm.email}
                  onChange={(e) => setSiteForm({ ...siteForm, email: e.target.value })}
                  data-testid="site-input-email"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <Switch
                checked={siteForm.attiva}
                onCheckedChange={(v) => setSiteForm({ ...siteForm, attiva: v })}
                id="site-attiva"
                data-testid="site-input-attiva"
              />
              <Label htmlFor="site-attiva" className="text-sm cursor-pointer">
                Sede attiva
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSiteOpen(false)} disabled={siteSaving}>Annulla</Button>
            <Button onClick={submitSite} disabled={siteSaving} data-testid="site-submit">
              {siteSaving ? "Salvataggio…" : (siteEditingId ? "Salva modifiche" : "Crea")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sprint 4H-DEP.B — Dialog gestione depositi della sede */}
      <DepositiSedeDialog
        siteId={depositiSiteId}
        siteName={sites.find((s) => s.id === depositiSiteId)?.nome}
        onClose={() => setDepositiSiteId(null)}
        confirm={confirm}
      />

      {/* Sprint 4SED.C — Dialog "Aggiungi azienda alla sede" */}
      <Dialog open={!!addCompanyDialog} onOpenChange={(o) => { if (!o) { setAddCompanyDialog(null); setAddCompanyId(""); } }}>
        <DialogContent data-testid="add-company-to-site-dialog">
          <DialogHeader>
            <DialogTitle>Aggiungi azienda alla sede</DialogTitle>
            <DialogDescription>
              Collega un'altra azienda a questa sede. La sede diventerà condivisa
              e comparirà anche sotto l'azienda aggiunta.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Azienda da collegare</Label>
            <select
              className="w-full h-9 text-sm border rounded-md px-2 bg-white"
              value={addCompanyId}
              onChange={(e) => setAddCompanyId(e.target.value)}
              data-testid="add-company-select"
            >
              <option value="">— Seleziona azienda —</option>
              {Object.entries(companiesMap)
                .filter(([cid]) => {
                  const site = sites.find((s) => s.id === addCompanyDialog?.siteId);
                  if (!site) return false;
                  return !getCompanyIds(site).includes(cid);
                })
                .sort(([, a], [, b]) => a.localeCompare(b))
                .map(([cid, label]) => (
                  <option key={cid} value={cid} data-testid={`add-company-opt-${cid}`}>{label}</option>
                ))}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddCompanyDialog(null); setAddCompanyId(""); }} disabled={addingCompany}>
              Annulla
            </Button>
            <Button onClick={handleAddCompanyToSite} disabled={!addCompanyId || addingCompany}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="add-company-submit">
              {addingCompany ? "Collegamento…" : "Collega"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sprint 4SED.C — Wizard travaso veicoli */}
      <TravasoVeicoliWizard
        open={!!travaso}
        onClose={() => setTravaso(null)}
        originSiteId={travaso?.siteId}
        originSiteName={travaso?.siteName}
        companyId={travaso?.companyId}
        companyName={travaso?.companyName}
        context={travaso?.context}
        onAnyTransferred={load}
        onAllTransferred={travaso?.onAfterTransfer}
      />
    </div>
  );
}

/**
 * Sprint 4F2-CVT.A — Sezione "Ritenuta acconto" della Configurazione Azienda.
 *
 * Default: aliquota 23% × base 50% → effettiva 11,50% (intermediazione
 * assicurativa, art. 25-bis DPR 600/73).
 *
 * Bottone "Salva" abilitato solo se almeno un campo è cambiato.
 * Usa l'endpoint dedicato /api/companies/{id}/config/ritenuta che scrive
 * audit log mirato.
 */
function RitenutaAccontoSection({ companyId, company, setCompany }) {
  const initialAttiva = !!(company?.ritenuta_attiva ?? true);
  const initialAliq = Number(company?.ritenuta_aliquota ?? 23);
  const initialBase = Number(company?.ritenuta_base_imponibile_pct ?? 50);

  const [attiva, setAttiva] = React.useState(initialAttiva);
  const [aliquota, setAliquota] = React.useState(initialAliq);
  const [base, setBase] = React.useState(initialBase);
  const [saving, setSaving] = React.useState(false);

  // Sync quando il `company` cambia dall'esterno (load iniziale o salva globale)
  React.useEffect(() => {
    setAttiva(!!(company?.ritenuta_attiva ?? true));
    setAliquota(Number(company?.ritenuta_aliquota ?? 23));
    setBase(Number(company?.ritenuta_base_imponibile_pct ?? 50));
  }, [company?.ritenuta_attiva, company?.ritenuta_aliquota, company?.ritenuta_base_imponibile_pct]);

  const effettiva = Math.round(aliquota * base) / 100;
  const dirty = (
    attiva !== initialAttiva
    || Number(aliquota) !== initialAliq
    || Number(base) !== initialBase
  );

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ritenuta_attiva: attiva,
        ritenuta_aliquota: String(aliquota),
        ritenuta_base_imponibile_pct: String(base),
      };
      const r = await api.patch(`/companies/${companyId}/config/ritenuta`, payload);
      // Aggiorna l'oggetto company in parent così "dirty" si resetta
      setCompany((c) => ({
        ...c,
        ritenuta_attiva: r.data.ritenuta_attiva,
        ritenuta_aliquota: r.data.ritenuta_aliquota,
        ritenuta_base_imponibile_pct: r.data.ritenuta_base_imponibile_pct,
      }));
      toast.success("Ritenuta acconto salvata");
    } catch (e) {
      toast.error(errMsg(e, "Errore salvataggio ritenuta"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sm:col-span-2 pt-3 border-t" data-testid="ritenuta-acconto-section">
      <div className="flex items-center justify-between mb-2">
        <div>
          <Label className="text-xs font-medium uppercase tracking-wider text-slate-500">
            Ritenuta acconto
          </Label>
          <p className="text-[11px] text-slate-500">
            Intermediazione assicurativa (art. 25-bis DPR 600/73). Default 23% × 50% = 11,50%.
          </p>
        </div>
        <Button
          variant="outline" size="sm"
          disabled={!dirty || saving}
          onClick={save}
          data-testid="ritenuta-save-btn">
          {saving ? "Salvataggio…" : "Salva ritenuta"}
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
        <div className="flex items-center gap-2">
          <CampoLabel campo="ritenuta_attiva">Ritenuta attiva</CampoLabel>
          <input
            type="checkbox" className="h-4 w-4 accent-blue-600"
            checked={attiva}
            onChange={(e) => setAttiva(e.target.checked)}
            data-testid="ritenuta-attiva-toggle"
          />
        </div>
        <div>
          <CampoLabel campo="ritenuta_aliquota">Aliquota ritenuta</CampoLabel>
          <div className="flex items-center gap-1">
            <Input
              type="number" min="0" max="100" step="0.01"
              value={aliquota}
              onChange={(e) => setAliquota(e.target.value === "" ? 0 : Number(e.target.value))}
              disabled={!attiva}
              data-testid="ritenuta-aliquota-input"
              className="font-mono"
            />
            <span className="text-sm text-slate-500">%</span>
          </div>
        </div>
        <div>
          <CampoLabel campo="ritenuta_base_imponibile_pct">Base imponibile</CampoLabel>
          <div className="flex items-center gap-1">
            <Input
              type="number" min="0" max="100" step="0.01"
              value={base}
              onChange={(e) => setBase(e.target.value === "" ? 0 : Number(e.target.value))}
              disabled={!attiva}
              data-testid="ritenuta-base-input"
              className="font-mono"
            />
            <span className="text-sm text-slate-500">%</span>
          </div>
        </div>
      </div>
      <div className="mt-2 p-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm flex items-center gap-2"
           data-testid="ritenuta-effettiva-display">
        <span className="font-medium">Aliquota effettiva:</span>
        <span className="font-mono text-base">
          {effettiva.toFixed(2).replace(".", ",")}%
        </span>
        <span className="text-xs text-amber-700 ml-1">
          ({aliquota}% × {base}% / 100)
        </span>
      </div>
    </div>
  );
}


/**
 * Sprint AZIENDA-RIFINITURE — Sezione "Referente Azienda".
 * Campi opzionali: cognome, nome, email, telefono.
 * Persisting via il salvataggio principale ("Salva" header) → patch_company.
 */
function ReferenteAziendaSection({ company, setCompany }) {
  return (
    <div className="sm:col-span-2 pt-3 border-t" data-testid="referente-azienda-section">
      <div className="flex items-center gap-2 mb-2">
        <UserRound className="h-4 w-4 text-slate-500" />
        <Label className="text-xs font-medium uppercase tracking-wider text-slate-500">
          Referente Azienda
        </Label>
      </div>
      <p className="text-[11px] text-slate-500 mb-2">
        Contatto principale per supporto e comunicazioni.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <CampoLabel campo="referente_cognome">Cognome</CampoLabel>
          <Input
            value={company.referente_cognome || ""}
            onChange={(e) => setCompany({ ...company, referente_cognome: e.target.value })}
            data-testid="referente-cognome"
          />
        </div>
        <div>
          <CampoLabel campo="referente_nome">Nome</CampoLabel>
          <Input
            value={company.referente_nome || ""}
            onChange={(e) => setCompany({ ...company, referente_nome: e.target.value })}
            data-testid="referente-nome"
          />
        </div>
        <div>
          <CampoLabel campo="referente_email">Email</CampoLabel>
          <Input
            type="email"
            value={company.referente_email || ""}
            onChange={(e) => setCompany({ ...company, referente_email: e.target.value })}
            data-testid="referente-email"
          />
        </div>
        <div>
          <CampoLabel campo="referente_telefono">Telefono</CampoLabel>
          <Input
            value={company.referente_telefono || ""}
            onChange={(e) => setCompany({ ...company, referente_telefono: e.target.value })}
            data-testid="referente-telefono"
          />
        </div>
      </div>
    </div>
  );
}


/**
 * Sprint AZIENDA-RIFINITURE — Sezione "Conti Bancari" multipli.
 *
 * Backward compat: alla prima apertura, se conti_bancari è vuoto e i vecchi
 * campi iban/banca sono valorizzati, vengono mostrati come primo elemento
 * della lista (UI-only fallback). Al primo salvataggio la migrazione avviene
 * naturalmente.
 *
 * I vecchi campi `iban` e `banca` restano nel modello come legacy/deprecati
 * ma la UI non li espone più direttamente.
 */
function LogoDocumentiSection({ companyId, company, setCompany }) {
  const fileRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const hasLogo = !!company.logo_documenti_url;

  useEffect(() => {
    let revoked = "";
    if (!hasLogo) { setPreviewUrl(""); return; }
    (async () => {
      try {
        const r = await api.get(`/companies/${companyId}/logo-documenti`, { responseType: "blob" });
        revoked = URL.createObjectURL(r.data);
        setPreviewUrl(revoked);
      } catch { setPreviewUrl(""); }
    })();
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [companyId, company.logo_documenti_url, hasLogo]);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const okType = ["image/png", "image/jpeg"].includes(f.type);
    if (!okType) { toast.error("Formato non ammesso: solo PNG o JPG"); return; }
    if (f.size > 2 * 1024 * 1024) { toast.error("Il logo supera la dimensione massima di 2 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/companies/${companyId}/logo-documenti`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setCompany({ ...company, logo_documenti_url: r.data?.logo_documenti_url || "set" });
      toast.success("Logo caricato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore caricamento logo");
    } finally { setBusy(false); }
  };

  const onRemove = async () => {
    setBusy(true);
    try {
      await api.delete(`/companies/${companyId}/logo-documenti`);
      setCompany({ ...company, logo_documenti_url: "" });
      setPreviewUrl("");
      toast.success("Logo rimosso");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore rimozione logo");
    } finally { setBusy(false); }
  };

  return (
    <div className="sm:col-span-2 pt-3 border-t" data-testid="logo-documenti-section">
      <CampoLabel campo="logo_documenti_url" className="text-xs font-medium uppercase tracking-wider text-slate-500">
        Logo per i documenti di stampa
      </CampoLabel>
      <p className="text-[11px] text-slate-500 mt-1">
        Compare nell'intestazione dei PDF (mandato, contratti). PNG o JPG, massimo 2 MB.
      </p>
      <div className="mt-2 flex items-center gap-4">
        <div className="h-20 w-40 border border-slate-200 rounded bg-slate-50 flex items-center justify-center overflow-hidden">
          {previewUrl ? (
            <img src={previewUrl} alt="Logo documenti" className="max-h-full max-w-full object-contain"
                 data-testid="logo-documenti-preview" />
          ) : (
            <span className="text-[11px] text-slate-400">Nessun logo</span>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden"
                 onChange={onFile} data-testid="logo-documenti-input" />
          <Button type="button" variant="outline" size="sm" onClick={onPick} disabled={busy}
                  data-testid="logo-documenti-upload-btn">
            {hasLogo ? "Sostituisci immagine" : "Carica immagine"}
          </Button>
          {hasLogo && (
            <Button type="button" variant="outline" size="sm" onClick={onRemove} disabled={busy}
                    className="text-rose-600 hover:text-rose-700" data-testid="logo-documenti-remove-btn">
              Rimuovi
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}


function ParametriContrattualiSection({ company, setCompany }) {
  const pc = company.parametri_contrattuali && typeof company.parametri_contrattuali === "object"
    ? company.parametri_contrattuali : {};
  const setPC = (patch) => setCompany({ ...company, parametri_contrattuali: { ...pc, ...patch } });
  const numChange = (key) => (e) => {
    const raw = e.target.value;
    setPC({ [key]: raw === "" ? null : Number(raw) });
  };
  return (
    <div className="sm:col-span-2 pt-3 border-t" data-testid="parametri-contrattuali-section">
      <Label className="text-xs font-medium uppercase tracking-wider text-slate-500">Parametri contrattuali predefiniti</Label>
      <p className="text-[11px] text-slate-500 mt-1 mb-2">
        Valori proposti di default dalla futura maschera di stampa. Non applicati retroattivamente.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <CampoLabel campo="parametri_contrattuali.giorni_validita_proposta">Giorni validità proposta</CampoLabel>
          <Input type="number" value={pc.giorni_validita_proposta ?? ""} onChange={numChange("giorni_validita_proposta")}
            data-testid="pc-giorni-validita-proposta" />
        </div>
        <div>
          <CampoLabel campo="parametri_contrattuali.spese_istruttoria">Spese di istruttoria (€)</CampoLabel>
          <Input type="number" step="0.01" value={pc.spese_istruttoria ?? ""} onChange={numChange("spese_istruttoria")}
            data-testid="pc-spese-istruttoria" />
        </div>
        <div className="flex items-center gap-2 pt-5">
          <Switch checked={!!pc.recesso_sedi_fisiche}
            onCheckedChange={(v) => setPC({ recesso_sedi_fisiche: v })}
            data-testid="pc-recesso-sedi-fisiche" />
          <CampoLabel campo="parametri_contrattuali.recesso_sedi_fisiche">Recesso nelle sedi fisiche</CampoLabel>
        </div>
        {pc.recesso_sedi_fisiche && (
          <div>
            <CampoLabel campo="parametri_contrattuali.giorni_recesso_sedi">Giorni di recesso (sedi fisiche)</CampoLabel>
            <Input type="number" value={pc.giorni_recesso_sedi ?? ""} onChange={numChange("giorni_recesso_sedi")}
              data-testid="pc-giorni-recesso-sedi" />
          </div>
        )}
        <div>
          <CampoLabel campo="parametri_contrattuali.giorni_recesso_fuori_sede">Giorni recesso fuori sede (min 14)</CampoLabel>
          <Input type="number" min="14" value={pc.giorni_recesso_fuori_sede ?? ""} onChange={numChange("giorni_recesso_fuori_sede")}
            data-testid="pc-giorni-recesso-fuori-sede" />
        </div>
        <div>
          <CampoLabel campo="parametri_contrattuali.mesi_garanzia_legale">Mesi garanzia legale (12–24)</CampoLabel>
          <Input type="number" min="12" max="24" value={pc.mesi_garanzia_legale ?? ""} onChange={numChange("mesi_garanzia_legale")}
            data-testid="pc-mesi-garanzia-legale" />
        </div>
        <div>
          <CampoLabel campo="parametri_contrattuali.penale_revoca_mandato">Penale revoca mandato (€)</CampoLabel>
          <Input type="number" step="0.01" value={pc.penale_revoca_mandato ?? ""} onChange={numChange("penale_revoca_mandato")}
            data-testid="pc-penale-revoca-mandato" />
        </div>
        <div>
          <CampoLabel campo="parametri_contrattuali.compenso_vendita_diretta_mandato">Compenso vendita diretta del proprietario (€)</CampoLabel>
          <Input type="number" step="0.01" value={pc.compenso_vendita_diretta_mandato ?? ""} onChange={numChange("compenso_vendita_diretta_mandato")}
            data-testid="pc-compenso-vendita-diretta-mandato" />
        </div>
        <div>
          <CampoLabel campo="parametri_contrattuali.giorni_disdetta_mandato">Giorni disdetta mandato</CampoLabel>
          <Input type="number" value={pc.giorni_disdetta_mandato ?? ""} onChange={numChange("giorni_disdetta_mandato")}
            data-testid="pc-giorni-disdetta-mandato" />
        </div>
      </div>
    </div>
  );
}


function ContiBancariSection({ company, setCompany }) {
  const list = React.useMemo(() => {
    const cb = Array.isArray(company.conti_bancari) ? company.conti_bancari : [];
    if (cb.length > 0) return cb;
    // Backward compat: seed primo conto dai vecchi iban/banca singoli
    if (company.iban || company.banca) {
      return [{
        iban: company.iban || "",
        banca: company.banca || "",
        descrizione: "Principale (legacy)",
      }];
    }
    return [];
  }, [company.conti_bancari, company.iban, company.banca]);

  const updateList = (next) => {
    setCompany({ ...company, conti_bancari: next });
  };

  const addRow = () => {
    updateList([...list, { iban: "", banca: "", descrizione: "" }]);
  };

  const removeRow = (idx) => {
    const next = list.slice();
    next.splice(idx, 1);
    updateList(next);
  };

  const setRow = (idx, patch) => {
    const next = list.slice();
    next[idx] = { ...next[idx], ...patch };
    updateList(next);
  };

  return (
    <div className="sm:col-span-2 pt-3 border-t" data-testid="conti-bancari-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Landmark className="h-4 w-4 text-slate-500" />
          <Label className="text-xs font-medium uppercase tracking-wider text-slate-500">
            Conti Bancari
          </Label>
        </div>
        <Button
          variant="outline" size="sm"
          onClick={addRow}
          data-testid="conto-bancario-add-btn">
          <Plus className="h-4 w-4 mr-1" /> Aggiungi Banca
        </Button>
      </div>
      {list.length === 0 ? (
        <p className="text-[12px] text-slate-400 italic py-3">
          Nessun conto bancario. Aggiungi il primo con "+ Aggiungi Banca".
        </p>
      ) : (
        <div className="space-y-2">
          {list.map((row, idx) => (
            <div
              key={idx}
              className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end p-2 rounded-md border border-slate-200 bg-slate-50/50"
              data-testid={`conto-bancario-row-${idx}`}
            >
              <div className="sm:col-span-5">
                <CampoLabel campo="conti_bancari.iban">IBAN</CampoLabel>
                <Input
                  value={row.iban || ""}
                  onChange={(e) => setRow(idx, { iban: e.target.value })}
                  className="font-mono text-xs"
                  data-testid={`conto-bancario-iban-${idx}`}
                />
              </div>
              <div className="sm:col-span-3">
                <CampoLabel campo="conti_bancari.banca">Banca</CampoLabel>
                <Input
                  value={row.banca || ""}
                  onChange={(e) => setRow(idx, { banca: e.target.value })}
                  data-testid={`conto-bancario-banca-${idx}`}
                />
              </div>
              <div className="sm:col-span-3">
                <CampoLabel campo="conti_bancari.descrizione">Descrizione</CampoLabel>
                <Input
                  value={row.descrizione || ""}
                  onChange={(e) => setRow(idx, { descrizione: e.target.value })}
                  placeholder="es. Principale, Provvigioni…"
                  data-testid={`conto-bancario-descr-${idx}`}
                />
              </div>
              <div className="sm:col-span-1 flex justify-end">
                <button
                  onClick={() => removeRow(idx)}
                  className="text-rose-600 hover:text-rose-700 p-1.5"
                  title="Rimuovi conto"
                  data-testid={`conto-bancario-remove-${idx}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * Sprint 4H-DEP.B — Gestione depositi FISICI di una sede.
 *
 * Mostra i depositi FISICI della sede (incl. "Sede Principale" auto-creato),
 * permette di aggiungerne, rinominarli, eliminarli (se vuoti).
 * I depositi di SISTEMA NON compaiono qui (sono globali, gestiti in
 * Impostazioni → Elenchi database).
 */
function DepositiSedeDialog({ siteId, siteName, onClose, confirm }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async (sid) => {
    if (!sid) return;
    setLoading(true);
    try {
      const r = await api.get(`/depositi?sede_id=${sid}&include_sistema=false`);
      setItems(r.data || []);
    } catch (e) {
      toast.error("Errore caricamento depositi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (siteId) load(siteId);
    else {
      setItems([]); setNewName(""); setEditingId(null); setDraftName("");
    }
  }, [siteId]);

  const create = async () => {
    const nome = newName.trim();
    if (!nome) { toast.error("Inserisci un nome"); return; }
    setCreating(true);
    try {
      await api.post(`/depositi`, { nome, sede_id: siteId });
      toast.success("Deposito creato");
      setNewName("");
      await load(siteId);
    } catch (e) {
      toast.error(errMsg(e, "Errore creazione deposito"));
    } finally {
      setCreating(false);
    }
  };

  const saveEdit = async (id) => {
    const nome = draftName.trim();
    if (!nome) { toast.error("Nome obbligatorio"); return; }
    setSavingEdit(true);
    try {
      await api.patch(`/depositi/${id}`, { nome });
      toast.success("Deposito rinominato");
      setEditingId(null); setDraftName("");
      await load(siteId);
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = (d) => {
    confirm({
      variant: "delete", title: "Elimina deposito",
      message: `Stai per eliminare il deposito "${d.nome}".`,
    }).then(async (ok) => {
      if (!ok) return;
      try {
        await api.delete(`/depositi/${d.id}`);
        toast.success("Deposito eliminato");
        await load(siteId);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (typeof detail === "string" && detail.includes("veicoli collegati")) {
          toast.error(detail, { duration: 6000 });
        } else {
          toast.error(detail || "Errore eliminazione");
        }
      }
    });
  };

  return (
    <Dialog open={!!siteId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="depositi-sede-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Warehouse className="h-5 w-5 text-blue-600" />
            Depositi della sede {siteName ? `— ${siteName}` : ""}
          </DialogTitle>
          <DialogDescription>
            I depositi FISICI sono legati a questa sede. Includono il "Sede Principale"
            auto-creato. I depositi di sistema (globali) si gestiscono in
            <em> Impostazioni → Elenchi database</em>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Card className="p-3">
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Aggiungi deposito
            </Label>
            <div className="flex gap-2 mt-1.5">
              <Input
                value={newName}
                placeholder="Es. Piazzale esterno, Officina backyard…"
                onChange={(e) => setNewName(e.target.value)}
                className="h-9 text-sm flex-1"
                data-testid="dep-add-input"
                onKeyDown={(e) => { if (e.key === "Enter") create(); }}
              />
              <Button onClick={create} disabled={creating || !newName.trim()}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="dep-add-btn">
                <Plus className="h-4 w-4 mr-1" /> Crea
              </Button>
            </div>
          </Card>

          <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
            {loading ? (
              <div className="p-4 text-sm text-slate-400 italic">Caricamento…</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">
                Nessun deposito. Aggiungi sopra.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5">Nome</th>
                    <th className="px-4 py-2.5 text-center w-[100px]">Stato</th>
                    <th className="px-4 py-2.5 w-[110px]"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((d) => (
                    <tr key={d.id} className="hover:bg-slate-50"
                        data-testid={`dep-row-${d.id}`}>
                      <td className="px-4 py-2.5">
                        {editingId === d.id ? (
                          <Input
                            value={draftName}
                            onChange={(e) => setDraftName(e.target.value)}
                            className="h-7 text-sm"
                            autoFocus
                            data-testid={`dep-edit-input-${d.id}`}
                          />
                        ) : (
                          <span className="font-medium text-slate-800">{d.nome}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        {d.attivo === false ? (
                          <span className="text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">Disattivo</span>
                        ) : (
                          <span className="text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Attivo</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center gap-3 justify-end">
                          {editingId === d.id ? (
                            <>
                              <button
                                onClick={() => saveEdit(d.id)}
                                disabled={savingEdit}
                                className="text-blue-600 hover:text-blue-700"
                                title="Salva"
                                data-testid={`dep-save-${d.id}`}
                              >
                                <Save className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => { setEditingId(null); setDraftName(""); }}
                                className="text-slate-500 hover:text-slate-700"
                                title="Annulla"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => { setEditingId(d.id); setDraftName(d.nome); }}
                                className="text-slate-500 hover:text-blue-600"
                                title="Rinomina"
                                data-testid={`dep-edit-${d.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(d)}
                                className="text-rose-600 hover:text-rose-700"
                                title="Elimina"
                                data-testid={`dep-del-${d.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="depositi-sede-close">
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

